/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import {
	Check as CheckIcon,
	Delete as DeleteIcon
} from '@mui/icons-material';
import { Box, Button, Typography, Tooltip, Link } from '@mui/material';
import { useContext, type ReactNode } from 'react';
import { useTheme, type Palette } from '@mui/material/styles';
import { PluginPremiumBadge } from '../../PluginBadges/PluginBadges';
import { GTMContext } from '../../../contexts/GTMContext';
import { GTM_EVENT_UPGRADE_PLAN } from '../../../constants/GTMEventNames';
import type { PremiumFeaturesSectionProps } from './types';
import { CustomerPortalContext, CustomerPortalAction } from '../../../contexts/CustomerPortalContext';
import { CreateAccountBanner } from '../../SnippetsContainer/CreateAccountBanner/CreateAccountBanner';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

export function PremiumFeaturesSection( {
	selectedPremiumFeatures,
	onEditorPluginsDeselect,
	onTabChange
}: PremiumFeaturesSectionProps ): ReactNode {
	const { isLoggedIn, featuresAvailableInPlan, plan, isTrialActive } = useContext( CustomerPortalContext );
	const { palette } = useTheme();

	const allFeaturesAvailableInPlan = selectedPremiumFeatures.every( feature => featuresAvailableInPlan.includes( feature.name ) );
	const noPremiumFeaturesSelected = selectedPremiumFeatures.length === 0;

	return (
		<Box>
			<CreateAccountBanner
				allFeaturesAvailableInPlan={ allFeaturesAvailableInPlan }
				arePremiumFeaturesSelected={ !!selectedPremiumFeatures.length }
				signUpVariant='sidebar'
				hideButtons={ true }
			/>

			{ !isLoggedIn && <UserUnauthorizedSection/> }
			{ isLoggedIn && ( !allFeaturesAvailableInPlan || plan === 'free' ) && !isTrialActive && <UserUpgradePlanSection/> }

			{ selectedPremiumFeatures.length > 0 &&
				<Box sx={ { display: 'flex', marginTop: '10px' } }>
					<Typography color={ 'text.primary' } fontSize={ '14px' } fontWeight={ 500 } lineHeight={ '22px' }>
						Premium features&nbsp;
					</Typography>
					<PluginPremiumBadge onlyIcon/>
					<Typography color={ 'text.primary' } fontSize={ '14px' } fontWeight={ 500 } lineHeight={ '22px' }>
						&nbsp;you have selected:
					</Typography>
				</Box>
			}

			<Box sx={ { display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' } }>
				{ selectedPremiumFeatures.map( feature => {
					const featureAvailableInPlan = featuresAvailableInPlan.includes( feature.name );

					return (
						<Box
							key={ feature.id }
							sx={ {
								border: `1px solid ${ getFeatureBorderColor( { palette, isLoggedIn, featureAvailableInPlan } ) }`,
								backgroundColor: getFeatureBackgroundColor( { palette, isLoggedIn, featureAvailableInPlan } ),
								borderRadius: '4px',
								padding: '0 8px'
							} }
						>
							<Tooltip
								arrow
								disableInteractive
								title={ getFeatureTooltipText( {
									isLoggedIn,
									featureAvailableInPlan,
									isTrialActive
								} ) }
							>
								<Box sx={ { display: 'flex', alignItems: 'center', gap: '4px' } }>
									{
										isLoggedIn && featureAvailableInPlan &&
										<CheckIcon sx={ { height: '15px', width: '15px' } } htmlColor={ palette.success.main }/>
									}
									<Typography color={ 'text.secondary' } fontSize={ '12px' } fontWeight={ 400 } lineHeight={ '20px' }>
										{ feature.name }
									</Typography>
									{
										isLoggedIn && !featureAvailableInPlan &&
										<DeleteIcon
											sx={ { height: '15px', width: '15px', cursor: 'pointer' } }
											htmlColor={ palette.warning.dark }
											onClick={ () => onEditorPluginsDeselect( feature.plugins.map( plugin => plugin.className ) ) }
										/>
									}
								</Box>
							</Tooltip>
						</Box>
					);
				} ) }
			</Box>

			{ !isLoggedIn && noPremiumFeaturesSelected &&
				<TextContainer dataTestId={ 'pf-section-not-logged-no-premium' }>
					<Text>
						It's that simple!
						Create an account to explore over <b>21 Premium Features</b> for <b>14 days</b> for free (credit card not required).
					</Text>
					<Text>
						Want to learn more about the premium options? Just look for the features marked with a star
						<PluginPremiumBadge onlyIcon isInline/> in the { ' ' }
						<Link data-testid={ 'features-tab-link-no-p' } sx={ { cursor: 'pointer' } } onClick={ () => onTabChange( 1 ) }>
							Features tab
						</Link>.
					</Text>
				</TextContainer>
			}

			{ isTrialActive && noPremiumFeaturesSelected &&
			<TextContainer dataTestId={ 'pf-section-trial-no-premium' }>
				<Text>
					You have access to <b>Premium Features</b> for 14 days for free, but you haven't selected any yet. { ' ' }
					Go back to the { ' ' }
					<Link data-testid={ 'features-tab-link-trial-no-p' } sx={ { cursor: 'pointer' } } onClick={ () => onTabChange( 1 ) }>
						Features tab
					</Link> { ' ' }
					and look for the features marked with a star <PluginPremiumBadge onlyIcon isInline/>.
				</Text>
			</TextContainer>
			}

			{ !isTrialActive && plan === 'free' && noPremiumFeaturesSelected &&
			<TextContainer dataTestId={ 'pf-section-no-trial-free-plan' }>
				<Text>
					Consider using our premium features to unlock advanced editor capabilities. Want to learn more? { ' ' }
					Look for the features marked with a star in the <PluginPremiumBadge onlyIcon isInline/> { ' ' }
					<Link data-testid={ 'features-tab-link-free' } sx={ { cursor: 'pointer' } } onClick={ () => onTabChange( 1 ) }>
						Features tab
					</Link>.
				</Text>
			</TextContainer>
			}

			{ !isTrialActive && ( plan === 'basic' || plan === 'premium' ) && noPremiumFeaturesSelected &&
			<TextContainer dataTestId={ 'pf-section-no-trial-basic-premium-plan' }>
				<Text>
					You have access to <b>Premium Features</b>, but you haven't selected any yet. Go back to the { ' ' }
					<Link data-testid={ 'features-tab-link-basic' } sx={ { cursor: 'pointer' } } onClick={ () => onTabChange( 1 ) }>
						Features tab
					</Link> { ' ' }
					and look for the features marked with a star <PluginPremiumBadge onlyIcon isInline/>.
				</Text>
			</TextContainer>
			}
		</Box>
	);
}

function getFeatureBorderColor( {
	palette,
	isLoggedIn,
	featureAvailableInPlan
}: {
	palette: Palette;
	isLoggedIn: boolean;
	featureAvailableInPlan: boolean;
} ): string {
	if ( !isLoggedIn ) {
		return palette.secondary[ 100 ]!;
	}

	if ( !featureAvailableInPlan ) {
		return palette.warning.light;
	}

	return palette.success.light;
}

function getFeatureBackgroundColor( {
	palette,
	isLoggedIn,
	featureAvailableInPlan
}: {
	palette: Palette;
	isLoggedIn: boolean;
	featureAvailableInPlan: boolean;
} ): string | null {
	if ( isLoggedIn && !featureAvailableInPlan ) {
		return palette.warning[ 100 ]!;
	}

	return null;
}

function getFeatureTooltipText( {
	isLoggedIn,
	featureAvailableInPlan,
	isTrialActive
}: {
	isLoggedIn: boolean;
	featureAvailableInPlan: boolean;
	isTrialActive: boolean;
} ): string | null {
	if ( !isLoggedIn ) {
		return null;
	}

	if ( isTrialActive ) {
		return null;
	}

	if ( !featureAvailableInPlan ) {
		return 'Feature not covered by your plan. Consider upgrade.';
	}

	return 'Feature included in your plan.';
}

function UserUnauthorizedSection() {
	const { performCustomerPortalAction } = useContext( CustomerPortalContext );
	const { onSelectedInstallMethodChange, onSelectedCodeOutputChange } = useContext( InstallationSettingsContext );

	function handleLogInClick() {
		performCustomerPortalAction( CustomerPortalAction.LOG_IN, {
			onSuccess: () => {
				onSelectedInstallMethodChange( null );
				onSelectedCodeOutputChange( null );
			}
		} );
	}

	function handleSignUpClick() {
		performCustomerPortalAction( CustomerPortalAction.SIGN_UP, {
			variant: 'sidebar',
			onSuccess: () => {
				onSelectedInstallMethodChange( null );
				onSelectedCodeOutputChange( null );
			}
		} );
	}

	return (
		<Box sx={ { display: 'grid', gridTemplateColumns: 'repeat( 2, 6fr )', gap: '8px', marginTop: '8px', height: '40px' } }>
			<Button
				data-testid='log-in-button'
				sx={ { fontWeight: '400', lineHeight: '22px', fontSize: '14px' } }
				variant={ 'outlined' }
				onClick={ handleLogInClick }
			>
				Log in
			</Button>
			<Button
				data-testid='sign-up-button'
				sx={ { fontWeight: '400', lineHeight: '22px', fontSize: '14px' } }
				variant={ 'contained' }
				onClick={ handleSignUpClick }
			>
				Sign up for trial
			</Button>
		</Box>
	);
}

function UserUpgradePlanSection() {
	const { triggerGTMEvent } = useContext( GTMContext );
	const { customerPortalAccount, getUpgradePlanUrl } = useContext( CustomerPortalContext );

	const organizationId = customerPortalAccount?.organizations?.at( 0 )?.id;
	const subscriptionId = customerPortalAccount?.subscriptions?.at( 0 )?.details.id;

	function handleUpgradePlanClick() {
		triggerGTMEvent( {
			event: GTM_EVENT_UPGRADE_PLAN
		} );
	}

	return (
		<Box sx={ { display: 'flex', height: '40px' } }>
			<Button
				data-testid='premium-upgrade-plan-button'
				sx={ { fontWeight: '400', lineHeight: '22px', fontSize: '14px', width: '100%' } }
				variant={ 'contained' }
				target='_blank'
				href={ getUpgradePlanUrl( organizationId!, subscriptionId ) }
				onClick={ handleUpgradePlanClick }
			>
				Upgrade your plan!
			</Button>
		</Box>
	);
}

function TextContainer( { children, dataTestId }: { children: ReactNode; dataTestId: string } ) {
	return (
		<Box display={ 'flex' } flexDirection={ 'column' } gap={ '5px' } marginTop={ '16px' } data-testid={ dataTestId } >
			{ children }
		</Box>
	);
}

function Text( { children }: { children: ReactNode } ) {
	return (
		<Typography color={ 'text.primary' } fontSize={ '14px' } fontWeight={ 400 } lineHeight={ '22px' } display={ 'inline' }>
			{ children }
		</Typography>
	);
}
